import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Zap,
  PlayCircle,
  Users,
  History,
  Settings,
  X,
  MessageCircle,
  Link2,
  Clock,
  CalendarDays,
  FolderOpen,
  ClipboardList,
  UsersRound,
  ChevronDown,
  ChevronRight,
  BarChart2,
  LineChart,
  Shield,
  UserCog,
  DollarSign,
  Landmark,
  ShoppingCart,
  Receipt,
  PiggyBank,
  Wallet,
  Briefcase,
  Command,
  Lightbulb,
  AlertTriangle,
  RefreshCw,
  Tag,
  AlertCircle,
  Instagram,
  BookOpen,
  Newspaper,
  Megaphone,
  Calendar,
  Target,
  MessageSquare,
  FileText,
  UserCheck,
  UserPlus,
  Bell,
  GraduationCap,
  Banknote,
  Radio,
  Timer,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { healthAPI, whatsappAPI } from '@/shared/lib/api';
import { Badge } from '@travellegacy/nexops-design-system';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Permission, hasPermission, hasAnyPermission, UserPermissions } from '@/shared/types/permissions';
import { useIsCurrentUserApprover } from '@/features/incident-management/hooks/useApprovers';
import { InstanceDetailsModal } from '@/shared/components/InstanceDetailsModal';

// ==========================================
// Tipos
// ==========================================

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

interface NavGroup {
  name: string;
  icon: React.ElementType;
  children: NavEntry[];
}

type NavEntry = NavItem | NavGroup;

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

// ==========================================
// Navegação Admin/User (com permissões granulares)
// ==========================================

const getAdminNavigation = (
  userRole: string,
  permissions: UserPermissions | undefined,
  isApprover: boolean = false
): NavEntry[] => {
  const nav: NavEntry[] = [];

  // Dashboard principal sempre visível para ADMIN e GESTOR
  if (userRole === 'ADMIN' || userRole === 'GESTOR') {
    nav.push({ name: 'Dashboard', href: '/', icon: LayoutDashboard });
  }

  // People Core - requer permissão PEOPLE_CORE
  if (hasPermission(userRole, permissions, Permission.PEOPLE_CORE)) {
    const peopleCoreItems: NavItem[] = [];

    // Itens básicos do People Core
    peopleCoreItems.push({ name: 'Meu Ponto', href: '/people-core/ponto', icon: Clock });
    peopleCoreItems.push({ name: 'Visão Geral', href: '/people-core/dashboard', icon: BarChart2 });
    peopleCoreItems.push({ name: 'Folha de Ponto', href: '/people-core/timesheet', icon: CalendarDays });
    peopleCoreItems.push({ name: 'Ajustes de Ponto', href: '/people-core/ajustes', icon: ClipboardList });
    peopleCoreItems.push({ name: 'Projetos', href: '/people-core/projetos', icon: FolderOpen });

    // Itens de gestão para quem tem permissão PEOPLE_CORE_MANAGEMENT
    if (hasPermission(userRole, permissions, Permission.PEOPLE_CORE_MANAGEMENT)) {
      peopleCoreItems.push({ name: 'Métricas da Equipe', href: '/people-core/metricas', icon: LineChart });
    }

    // Logs de Auditoria - apenas para ADMIN
    if (userRole === 'ADMIN') {
      peopleCoreItems.push({ name: 'Logs de Auditoria', href: '/people-core/audit-logs', icon: Shield });
    }

    nav.push({
      name: 'People Core',
      icon: UsersRound,
      children: peopleCoreItems,
    });
  }

  // Central de Problemas - requer permissão INCIDENT_MANAGEMENT
  if (hasPermission(userRole, permissions, Permission.INCIDENT_MANAGEMENT)) {
    const incidentItems: NavItem[] = [
      { name: 'Estatísticas', href: '/incident-management/statistics', icon: BarChart2 },
      { name: 'Incidentes', href: '/incident-management', icon: AlertTriangle },
    ];

    // Solicitações Pendentes - apenas para aprovadores
    if (isApprover) {
      incidentItems.push({ name: 'Solicitações Pendentes', href: '/incident-management/pending-approvals', icon: AlertCircle });
    }

    incidentItems.push(
      { name: 'Acordos de Pagamento', href: '/incident-management/agreements', icon: DollarSign },
      { name: 'Solicitações de Reset', href: '/incident-management/reset-requests', icon: RefreshCw },
      { name: 'Categorias', href: '/incident-management/categories', icon: Tag }
    );

    // Configurações - apenas para ADMIN
    if (userRole === 'ADMIN') {
      incidentItems.push({ name: 'Configurações', href: '/incident-management/settings', icon: Settings });
    }

    nav.push({
      name: 'Central de Problemas',
      icon: AlertTriangle,
      children: incidentItems,
    });
  }

  // Automações - requer permissão AUTOMATIONS
  if (hasPermission(userRole, permissions, Permission.AUTOMATIONS)) {
    nav.push({
      name: 'Automações',
      icon: Zap,
      children: [
        { name: 'Dashboard', href: '/automations/dashboard', icon: LayoutDashboard },
        { name: 'Automações', href: '/automations', icon: Zap },
        { name: 'Execuções', href: '/executions', icon: PlayCircle },
      ],
    });
  }

  // Operações - requer WHATSAPP_OPERATIONS ou CLIENT_OPERATIONS
  if (hasAnyPermission(userRole, permissions, [Permission.WHATSAPP_OPERATIONS, Permission.CLIENT_OPERATIONS])) {
    const operationsChildren: NavItem[] = [];

    // Dashboard Operacional - disponível para CLIENT_OPERATIONS
    if (hasPermission(userRole, permissions, Permission.CLIENT_OPERATIONS)) {
      operationsChildren.push({ name: 'Dashboard', href: '/operations/dashboard', icon: BarChart2 });
      operationsChildren.push({ name: 'Onboarding', href: '/onboarding', icon: UserPlus });
      operationsChildren.push({ name: 'Demandas', href: '/demands', icon: Timer });
    }

    if (hasPermission(userRole, permissions, Permission.WHATSAPP_OPERATIONS)) {
      operationsChildren.push({ name: 'WhatsApp', href: '/whatsapp/groups', icon: MessageCircle });
      operationsChildren.push({ name: 'Grupos de Promoções', href: '/promotion-groups', icon: Megaphone });
    }

    // Ações em Massa - APENAS para CLIENT_OPERATIONS
    if (hasPermission(userRole, permissions, Permission.CLIENT_OPERATIONS)) {
      operationsChildren.push({ name: 'Ações em Massa', href: '/operations', icon: Command });
    }

    if (hasPermission(userRole, permissions, Permission.CLIENT_OPERATIONS)) {
      operationsChildren.push({ name: 'Links de Pagamento', href: '/custom-payment-links', icon: Link2 });
    }

    // Lembretes - disponível para todos os usuários autenticados
    operationsChildren.push({ name: 'Lembretes', href: '/reminders', icon: Bell });

    if (operationsChildren.length > 0) {
      nav.push({
        name: 'Operações',
        icon: Briefcase,
        children: operationsChildren,
      });
    }
  }

  // Treinamentos - requer permissão TRAINING
  if (hasPermission(userRole, permissions, Permission.TRAINING)) {
    nav.push({
      name: 'Treinamentos',
      icon: GraduationCap,
      children: [
        { name: 'Comunicação', href: '/training/communication', icon: MessageCircle },
        { name: 'Torre', href: '/training/torre', icon: Radio },
      ],
    });
  }

  // Marketing - agrupa Instagram e Blog (3 níveis)
  if (hasAnyPermission(userRole, permissions, [Permission.INSTAGRAM, Permission.BLOG])) {
    const marketingChildren: NavEntry[] = [];

    if (hasPermission(userRole, permissions, Permission.INSTAGRAM)) {
      marketingChildren.push({
        name: 'Instagram',
        icon: Instagram,
        children: [
          { name: 'Post X', href: '/instagram/post-x', icon: X },
        ],
      });
    }

    if (hasPermission(userRole, permissions, Permission.BLOG)) {
      marketingChildren.push({
        name: 'Blog',
        icon: BookOpen,
        children: [
          { name: 'Artigos', href: '/blog/articles', icon: Newspaper },
        ],
      });
    }

    if (marketingChildren.length > 0) {
      nav.push({
        name: 'Marketing',
        icon: Megaphone,
        children: marketingChildren,
      });
    }
  }

  // Feedback e PDI - requer permissão FEEDBACK_PDI
  if (hasPermission(userRole, permissions, Permission.FEEDBACK_PDI)) {
    const feedbackChildren: NavItem[] = [];

    // Dashboard e Feed de Feedbacks - apenas para gestores
    if (hasPermission(userRole, permissions, Permission.FEEDBACK_PDI_MANAGEMENT)) {
      feedbackChildren.push(
        { name: 'Dashboard', href: '/feedback-pdi/dashboard', icon: BarChart2 },
        { name: 'Feed de Feedbacks', href: '/feedback-pdi/feed', icon: MessageSquare }
      );
    }

    // Check-ins, PDI e Preparação - para todos com acesso ao módulo
    feedbackChildren.push(
      { name: 'Meus Check-ins', href: '/feedback-pdi/check-ins', icon: Calendar },
      { name: 'Meu PDI', href: '/feedback-pdi/pdi', icon: Target },
      { name: 'Preparação Check-in', href: '/feedback-pdi/preparation', icon: FileText }
    );

    nav.push({
      name: 'Feedback',
      icon: MessageCircle,
      children: feedbackChildren,
    });
  }

  // Financeiro - requer permissão FINANCE ou FINANCE_RECEIPT
  if (hasAnyPermission(userRole, permissions, [Permission.FINANCE, Permission.FINANCE_RECEIPT])) {
    const financialChildren: NavItem[] = [];

    // Se tem permissão FINANCE completa, mostra tudo
    if (hasPermission(userRole, permissions, Permission.FINANCE)) {
      financialChildren.push(
        { name: 'Dashboard', href: '/financial/dashboard', icon: BarChart2 },
        { name: 'DRE', href: '/financial/dre', icon: FileText },
        { name: 'Caixa', href: '/financial/cash-box', icon: Wallet },
        { name: 'Dívida', href: '/financial/partner-debt', icon: Landmark },
        { name: 'Receitas', href: '/financial/sales', icon: ShoppingCart },
        { name: 'Despesas', href: '/financial/operational-costs', icon: Receipt },
        { name: 'Comissões', href: '/financial/commissions', icon: PiggyBank },
        { name: 'Pagamentos', href: '/financial/payments', icon: Banknote },
        { name: 'Recibos', href: '/financial/receipts', icon: FileText }
      );
    }
    // Se tem apenas permissão FINANCE_RECEIPT, mostra só Recibos
    else if (hasPermission(userRole, permissions, Permission.FINANCE_RECEIPT)) {
      financialChildren.push(
        { name: 'Recibos', href: '/financial/receipts', icon: FileText }
      );
    }

    if (financialChildren.length > 0) {
      nav.push({
        name: 'Financeiro',
        icon: DollarSign,
        children: financialChildren,
      });
    }
  }

  // RH - requer permissão PEOPLE_CORE_MANAGEMENT
  if (hasPermission(userRole, permissions, Permission.PEOPLE_CORE_MANAGEMENT)) {
    nav.push({
      name: 'RH',
      icon: UserCog,
      children: [
        { name: 'Dashboard', href: '/people-core/hcm/dashboard', icon: BarChart2 },
        { name: 'Colaboradores', href: '/people-core/hcm/employees', icon: Users },
      ],
    });
  }

  // Clientes - visível para todos (ADMIN, GESTOR, COLLABORATOR)
  if (userRole === 'ADMIN' || userRole === 'GESTOR' || userRole === 'COLLABORATOR') {
    nav.push({ name: 'Clientes', href: '/clients', icon: UserCheck });
  }

  // Configurações - apenas ADMIN
  const configChildren: NavItem[] = [];

  // Usuários, Permissões e Acessos - apenas ADMIN
  if (userRole === 'ADMIN') {
    configChildren.push({ name: 'Usuários', href: '/users', icon: UserCog });
    configChildren.push({ name: 'Permissões', href: '/permissions', icon: Shield });
    configChildren.push({ name: 'Acessos', href: '/accesses', icon: History });
    configChildren.push({ name: 'Custos de IA', href: '/ai-usage', icon: Zap });
  }

  if (configChildren.length > 0) {
    nav.push({
      name: 'Configurações',
      icon: Settings,
      children: configChildren,
    });
  }

  // Sugestões - visível para todos (ADMIN, GESTOR, COLLABORATOR) - ÚLTIMA POSIÇÃO
  if (userRole === 'ADMIN' || userRole === 'GESTOR' || userRole === 'COLLABORATOR') {
    // Escolher o caminho correto baseado no role
    const suggestionsPath = userRole === 'COLLABORATOR'
      ? '/people-core/suggestions'
      : '/suggestions';

    nav.push({
      name: 'Sugestões',
      href: suggestionsPath,
      icon: Lightbulb
    });
  }

  return nav;
};

// ==========================================
// Componente NavLink simples
// ==========================================

function NavLink({ item, onClose }: { item: NavItem; onClose?: () => void }) {
  const location = useLocation();
  const isActive =
    item.href === '/'
      ? location.pathname === '/'
      : location.pathname === item.href || location.pathname.startsWith(item.href + '/');
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      onClick={onClose}
      className={cn(
        'flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover-menu'
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
      <span>{item.name}</span>
    </Link>
  );
}

// ==========================================
// Componente NavGroup com dropdown
// ==========================================

function NavGroupItem({ group, onClose }: { group: NavGroup; onClose?: () => void }) {
  const location = useLocation();

  // Função recursiva para verificar se algum filho (ou neto) está ativo
  const isAnyDescendantActive = (entries: NavEntry[]): boolean => {
    return entries.some((entry) => {
      if (isNavGroup(entry)) {
        return isAnyDescendantActive(entry.children);
      }
      return location.pathname === entry.href || location.pathname.startsWith(entry.href + '/');
    });
  };

  const isAnyChildActive = isAnyDescendantActive(group.children);
  const [open, setOpen] = useState(isAnyChildActive);
  const GroupIcon = group.icon;

  return (
    <div>
      {/* Cabeçalho do grupo */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isAnyChildActive
            ? 'text-foreground bg-accent/60'
            : 'text-muted-foreground hover-menu'
        )}
      >
        <span className="flex items-center space-x-3">
          <GroupIcon className="h-5 w-5 shrink-0" />
          <span>{group.name}</span>
        </span>
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 opacity-60" />
        )}
      </button>

      {/* Itens filhos */}
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-border space-y-0.5">
          {group.children.map((child) => {
            // Se for um grupo, renderiza recursivamente
            if (isNavGroup(child)) {
              return <NavGroupItem key={child.name} group={child} onClose={onClose} />;
            }

            // Se for um item, renderiza como link
            // Check if any OTHER child has a more specific match
            const hasMoreSpecificMatch = group.children.some(
              (otherChild) =>
                !isNavGroup(otherChild) &&
                otherChild.href !== child.href &&
                otherChild.href.startsWith(child.href) &&
                (location.pathname === otherChild.href ||
                  location.pathname.startsWith(otherChild.href + '/'))
            );

            // Only active if:
            // 1. Exact match, OR
            // 2. Starts with this href (with trailing slash), AND no other child has a more specific match
            const isActive =
              location.pathname === child.href ||
              (location.pathname.startsWith(child.href + '/') && !hasMoreSpecificMatch);
            const ChildIcon = child.icon;

            return (
              <Link
                key={child.href}
                to={child.href}
                onClick={onClose}
                className={cn(
                  'flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover-menu'
                )}
              >
                <ChildIcon className="h-4 w-4 shrink-0" />
                <span>{child.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==========================================
// Sidebar principal
// ==========================================

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen = true, onClose }: SidebarProps) {
  const { user } = useAuth();
  const [selectedInstance, setSelectedInstance] = useState<{
    name: string;
    key: 'saulo' | 'robo' | 'gabriel' | 'promocoes';
    data?: {
      instance: string;
      status: string;
      connected: boolean;
      phone?: string;
    };
  } | null>(null);

  // Verificar se o usuário atual é aprovador
  const { data: isApprover = false } = useIsCurrentUserApprover(user?.id);

  // COLLABORATOR agora usa getAdminNavigation com permissões, não mais menu fixo
  const navigation = getAdminNavigation(user?.role || '', user?.permissions, isApprover);

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const response = await healthAPI.check();
      return response.data;
    },
    refetchInterval: 30000,
  });

  // Usar novo endpoint otimizado
  // Polling mais rápido se houver instância selecionada em modo connecting
  const isConnecting = selectedInstance?.data?.status === 'connecting';
  const { data: whatsappInstances } = useQuery({
    queryKey: ['whatsapp-instances'],
    queryFn: async () => {
      const response = await whatsappAPI.getAllInstances();
      return response.data;
    },
    refetchInterval: isConnecting ? 3000 : 15000, // 3s durante conexão, 15s normal
  });

  const whatsappStatus = whatsappInstances?.instances;

  // Atualizar dados da instância selecionada quando a query atualizar
  useEffect(() => {
    if (selectedInstance && whatsappStatus) {
      const updatedData = whatsappStatus[selectedInstance.key];
      if (updatedData) {
        setSelectedInstance((prev) =>
          prev
            ? {
                ...prev,
                data: updatedData,
              }
            : null
        );
      }
    }
  }, [whatsappStatus, selectedInstance?.key]);

  return (
    <>
      {/* Overlay para mobile */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />}

      {/* Sidebar */}
      <div
        className={cn(
          'flex h-screen w-64 flex-col bg-card border-r transition-transform duration-300 ease-in-out',
          'fixed lg:static inset-y-0 left-0 z-50',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between lg:justify-center border-b px-4 lg:px-6">
          <Link to="/" className="flex items-center" onClick={onClose}>
            <img src="/ugoo-branco.svg" alt="NexOps" className="h-10 w-10" />
          </Link>

          {/* Botão fechar (apenas mobile) */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 hover-icon rounded-lg transition-colors"
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-0.5">
          {navigation.map((entry) =>
            isNavGroup(entry) ? (
              <NavGroupItem key={entry.name} group={entry} onClose={onClose} />
            ) : (
              <NavLink key={entry.href} item={entry} onClose={onClose} />
            )
          )}
        </nav>

        {/* Status Section - Fixed at Bottom */}
        <div className="border-t px-3 py-3 mt-auto">
          <div className="text-xs font-medium text-muted-foreground mb-2 px-1">
            Status do Sistema
          </div>
          <div className="space-y-2">
            {health && (
              <Badge
                variant={health.status === 'ok' ? 'success' : 'error'}
                className="w-full justify-start text-xs"
              >
                {health.status === 'ok' ? '✓ NexOps Online' : '✗ NexOps Offline'}
              </Badge>
            )}
            {whatsappStatus?.saulo && (
              <Badge
                variant={whatsappStatus.saulo.connected ? 'success' : 'error'}
                className="w-full justify-start text-xs cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setSelectedInstance({ name: 'Saulo', key: 'saulo', data: whatsappStatus.saulo })}
              >
                <MessageCircle className="h-3 w-3 mr-1.5" />
                {whatsappStatus.saulo.connected ? 'Saulo Online' : 'Saulo Offline'}
              </Badge>
            )}
            {whatsappStatus?.robo && (
              <Badge
                variant={whatsappStatus.robo.connected ? 'success' : 'error'}
                className="w-full justify-start text-xs cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setSelectedInstance({ name: 'Robo', key: 'robo', data: whatsappStatus.robo })}
              >
                <MessageCircle className="h-3 w-3 mr-1.5" />
                {whatsappStatus.robo.connected ? 'Robo Online' : 'Robo Offline'}
              </Badge>
            )}
            {whatsappStatus?.gabriel && (
              <Badge
                variant={whatsappStatus.gabriel.connected ? 'success' : 'error'}
                className="w-full justify-start text-xs cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setSelectedInstance({ name: 'Gabriel', key: 'gabriel', data: whatsappStatus.gabriel })}
              >
                <MessageCircle className="h-3 w-3 mr-1.5" />
                {whatsappStatus.gabriel.connected ? 'Gabriel Online' : 'Gabriel Offline'}
              </Badge>
            )}
            {whatsappStatus?.promocoes && (
              <Badge
                variant={whatsappStatus.promocoes.connected ? 'success' : 'error'}
                className="w-full justify-start text-xs cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setSelectedInstance({ name: 'Promoções', key: 'promocoes', data: whatsappStatus.promocoes })}
              >
                <Megaphone className="h-3 w-3 mr-1.5" />
                {whatsappStatus.promocoes.connected ? 'Promoções Online' : 'Promoções Offline'}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Modal de detalhes da instância */}
      {selectedInstance && (
        <InstanceDetailsModal
          isOpen={!!selectedInstance}
          onClose={() => setSelectedInstance(null)}
          instance={selectedInstance}
        />
      )}
    </>
  );
}
